import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

export type KcPersona = 'investor' | 'issuer';

/**
 * Intent persona switch for the knowledge centre — a clean segmented control (white thumb on a grey
 * track) that re-scopes the hub between investor and issuer. Two-way bindable via `[(persona)]`.
 *
 * Feature-local copy of the former `@pitchin/ui` `pin-kc-persona-switch` — the Knowledge Centre owns
 * its UI so the feature is independent of the design-system library's release state (ADR-0006).
 */
@Component({
  selector: 'kc-persona-switch',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="tw-inline-flex tw-bg-[#e7e8ea] tw-rounded-full tw-p-[4px]" role="group" aria-label="I want to…">
      <button
        type="button"
        (click)="select('investor')"
        [attr.aria-pressed]="persona === 'investor'"
        [class]="segClasses('investor')"
      >
        <svg [class]="iconClasses('investor')" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 17l6-6 4 4 8-8"/><path d="M21 7v5M21 7h-5"/></svg>
        {{ investorLabel }}
      </button>
      <button
        type="button"
        (click)="select('issuer')"
        [attr.aria-pressed]="persona === 'issuer'"
        [class]="segClasses('issuer')"
      >
        <svg [class]="iconClasses('issuer')" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6"/></svg>
        {{ issuerLabel }}
      </button>
    </div>
  `,
})
export class KcPersonaSwitchComponent {
  @Input() persona: KcPersona = 'investor';
  @Output() personaChange = new EventEmitter<KcPersona>();
  @Input() investorLabel = "I'm Investing";
  @Input() issuerLabel = "I'm Fundraising";

  select(p: KcPersona): void {
    if (p !== this.persona) {
      this.persona = p;
      this.personaChange.emit(p);
    }
  }

  segClasses(p: KcPersona): string {
    const base =
      'tw-flex tw-items-center tw-gap-[7px] tw-border-none tw-font-semibold tw-text-[13.5px] tw-px-[20px] tw-py-[9px] tw-rounded-full tw-cursor-pointer tw-transition-all';
    return this.persona === p
      ? `${base} tw-bg-white tw-text-[#1a1a1a] tw-shadow-[0_1px_3px_rgba(0,0,0,0.14)]`
      : `${base} tw-bg-transparent tw-text-[#7a7a7a]`;
  }

  iconClasses(p: KcPersona): string {
    return this.persona === p ? 'tw-text-primary' : 'tw-text-[#9aa0a6]';
  }
}
